import { useEffect, useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { useCalendarLookup } from '@/hooks/useCalendarLookup'

const DEFAULT_PAGE_SIZE = 20

interface CalendarLookupTableProps {
  /** Tailwind max-height for the scroll area (dialog vs full page). */
  scrollClassName?: string
}

/**
 * Searchable, READ-ONLY calendar lookup table (Calendar Year × Event × DC ×
 * PAW End × POG Complete). Filters are pushed to the backend; pagination is
 * server-side too. Shared by the value-chooser dialog and the standalone admin
 * page.
 */
export function CalendarLookupTable({
  scrollClassName = 'max-h-[380px]',
}: CalendarLookupTableProps) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.CALENDAR_LOOKUP_TABLE,
    DEFAULT_PAGE_SIZE
  )

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    setPage(1)
  }, [debounced, pageSize])

  // Heuristic: if the user types digits, treat as `year`; otherwise pass as
  // `event` since that's the most common search dimension.
  const params = useMemo(() => {
    const skip = (page - 1) * pageSize
    if (!debounced) return { skip, limit: pageSize }
    const asYear = /^\d{4}$/.test(debounced) ? Number(debounced) : undefined
    return {
      year: asYear,
      event: asYear ? undefined : debounced,
      skip,
      limit: pageSize,
    }
  }, [debounced, page, pageSize])

  const { data, isLoading, isError } = useCalendarLookup(params)

  const rows = data?.data ?? []
  const total = data?.pagination?.total ?? rows.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Filter by event, or 4-digit year..."
          className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      <div
        className={cn(
          'overflow-y-auto rounded-md border border-border',
          scrollClassName
        )}
      >
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="text-xs text-muted-foreground">
              <th className="border-b border-border px-3 py-2 text-left font-medium">
                Calendar Year
              </th>
              <th className="border-b border-border px-3 py-2 text-left font-medium">
                Event
              </th>
              <th className="border-b border-border px-3 py-2 text-left font-medium">
                DC
              </th>
              <th className="border-b border-border px-3 py-2 text-left font-medium">
                PAW End
              </th>
              <th className="border-b border-border px-3 py-2 text-left font-medium">
                POG Complete
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-4 text-center text-muted-foreground"
                >
                  Loading…
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-4 text-center text-destructive"
                >
                  Failed to load calendar lookup.
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-4 text-center text-muted-foreground"
                >
                  No matching rows.
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr key={row.id} className="border-t border-border/60">
                  <td className="px-3 py-1.5 tabular-nums text-foreground">
                    {row.year}
                  </td>
                  <td className="px-3 py-1.5 font-medium text-foreground">
                    {row.event}
                  </td>
                  <td className="px-3 py-1.5 text-foreground">{row.dc}</td>
                  <td className="px-3 py-1.5 tabular-nums text-foreground">
                    {row.paw_end ?? '—'}
                  </td>
                  <td className="px-3 py-1.5 tabular-nums text-foreground">
                    {row.pog_complete ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  )
}
